/* eslint-disable no-console */

// Manual PWA update / reset helpers (issue #223741).
//
// updateApplication() -> "soft" refresh: check for a new Service Worker and drop
//   the Cache Storage, then reload. localStorage / sessionStorage / IndexedDB are
//   left untouched so the user stays logged in and keeps local data.
//
// resetApplication() -> "hard" reset: unregister Service Workers and wipe Cache
//   Storage, localStorage, sessionStorage and IndexedDB, then reload. This logs
//   the user out and clears locally stored data. Use only when a plain update did
//   not fix the problem.

import { version as packageVersion } from "../../package.json";

export function getAppVersion() {
  return process.env.VUE_APP_VERSION || packageVersion || "";
}

async function clearAllCaches() {
  if (typeof caches === "undefined") return;
  const cacheNames = await caches.keys();
  await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
}

async function updateServiceWorkers() {
  if (!("serviceWorker" in navigator)) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(
    registrations.map((registration) =>
      registration.update().catch((error) => {
        console.warn("Service Worker update failed:", error);
      })
    )
  );
}

async function unregisterServiceWorkers() {
  if (!("serviceWorker" in navigator)) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => registration.unregister()));
}

async function deleteIndexedDbDatabases() {
  if (typeof indexedDB === "undefined") return;
  // indexedDB.databases() is not available in Firefox / older browsers.
  if (typeof indexedDB.databases !== "function") return;

  let databases = [];
  try {
    databases = await indexedDB.databases();
  } catch (error) {
    console.warn("Could not enumerate IndexedDB databases:", error);
    return;
  }

  await Promise.all(
    databases
      .map((database) => database.name)
      .filter(Boolean)
      .map(
        (name) =>
          new Promise((resolve) => {
            const request = indexedDB.deleteDatabase(name);
            request.onsuccess = resolve;
            request.onerror = resolve;
            request.onblocked = resolve;
          })
      )
  );
}

export async function updateApplication() {
  try {
    await updateServiceWorkers();
    await clearAllCaches();
  } catch (error) {
    console.error("Error while updating the application:", error);
  } finally {
    window.location.reload();
  }
}

export async function resetApplication() {
  try {
    await unregisterServiceWorkers();
    await clearAllCaches();

    try {
      localStorage.clear();
    } catch (error) {
      console.warn("Could not clear localStorage:", error);
    }
    try {
      sessionStorage.clear();
    } catch (error) {
      console.warn("Could not clear sessionStorage:", error);
    }

    await deleteIndexedDbDatabases();
  } catch (error) {
    console.error("Error while resetting the application:", error);
  } finally {
    window.location.reload();
  }
}
